import {
  buildAddCategory,
  buildAddExpense,
  buildBulkCategorize,
  buildDeleteCategory,
  buildDeleteExpense,
  buildEditExpense,
  buildRenameCategory,
  buildReorderCategories,
  buildRestoreCategory,
  buildRestoreExpense,
  buildRetireUnusedDefaults,
  buildSeedCategories,
  buildSetCategoryHidden,
  canDelete,
  editableFields,
  expenseUpdatedEvent,
  formatPeso,
  newId,
  payFromAccount,
  resolveDeductFund,
  singletonId,
  type BuildCtx,
  type DomainEvent,
  type ExpenseDraft,
  type ExpenseFields,
  type ExpensePatch,
  type Mutation,
} from '@tahi/core';
import { monotonicNow } from '../mutation';
import {
  buildSpendingDashboardTiles,
  SPENDING_DASHBOARD_ID,
} from '../../modules/spending/dashboardDefaults';
import { getBudgetSettingsView, listAccounts, listFunds } from '../repos/budget';
import * as repo from '../repos/spending';
import type { SpendingEntry } from '../repos/spending';
import { runCommand, type CommandEnv } from './dispatch';
import { PAYMENTS_CATEGORY_SEED } from './payments';
import { QUIT_CATEGORY_SEED } from './quit';

export type SpendingResult<T = object> = ({ ok: true } & T) | { ok: false; error: string };

/** Bump the version only if the default set changes and existing users should get the additions. */
export const SPENDING_SEED = 'spending.v1';
/** Marker for the one-time v0.1.1 cleanup of unused Cigarettes / Loan payment defaults. */
export const RETIRE_DEFAULTS_SEED = 'spending.retire_defaults.v0.1.1';

function ctx(env: CommandEnv): BuildCtx {
  return { now: monotonicNow(), tz: env.tz, newId };
}

/**
 * Conventions §5 through v0.1c's runCommand: primary mutations + rule mutations in one applyLocal,
 * then the connection toast ("Logged · Spending ₱250 ┄ Budget") if the chain spread past Spending.
 */
async function commit(
  env: CommandEnv,
  mutations: Mutation[],
  event?: DomainEvent,
  amountText?: string,
): Promise<void> {
  await runCommand(env, {
    mutations,
    ...(event ? { event } : {}),
    ...(amountText ? { toast: { headline: 'Logged', details: { spending: amountText } } } : {}),
  });
}

function fieldsOf(e: SpendingEntry): ExpenseFields {
  return {
    amountMinor: e.amountMinor,
    categoryId: e.categoryId,
    note: e.note,
    date: e.date,
    method: e.method,
    fundId: e.fundId,
    accountId: e.accountId,
  };
}

export async function addExpense(
  env: CommandEnv,
  draft: ExpenseDraft,
): Promise<SpendingResult<{ id: string }>> {
  // Budget (v0.2): the fund goes in the insert itself (picked, else the default while auto-deduct is on).
  const budget = env.usable('budget');
  const settings = budget ? await getBudgetSettingsView(env.db, env.userId) : null;
  const fundId = settings ? resolveDeductFund(draft.fundId, settings, true) : null;
  // v0.5: the "From" account: picked, else the fund's default account, else "Pay from".
  const funds = settings && fundId ? await listFunds(env.db, env.userId) : [];
  const accountId = settings
    ? payFromAccount(
        draft.accountId,
        funds.find((f) => f.id === fundId) ?? null,
        settings,
        true,
        await listAccounts(env.db, env.userId),
      )
    : null;
  const r = buildAddExpense({ ...draft, fundId, accountId }, ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation], r.event, formatPeso(draft.amountMinor));
  return { ok: true, id: r.entryId };
}

/** Returns the patch that undoes the edit ({} when nothing changed). */
export async function editExpense(
  env: CommandEnv,
  id: string,
  patch: ExpensePatch,
): Promise<SpendingResult<{ undo: ExpensePatch }>> {
  const entry = await repo.getEntry(env.db, env.userId, id);
  if (!entry || entry.deletedAt) return { ok: false, error: 'not_found' };
  const allowed = editableFields(entry) as readonly string[];
  const safe = Object.fromEntries(
    Object.entries(patch).filter(([k]) => allowed.includes(k)),
  ) as ExpensePatch;
  const c = ctx(env);
  const r = buildEditExpense(id, fieldsOf(entry), safe, c);
  if (!r.ok) return r;
  if (r.mutation) {
    // v0.2: one expense.updated per edit (old and new values, e.g. a fund move); no rules listen yet.
    const event = expenseUpdatedEvent(
      id,
      entry.sourceModule,
      r.undo,
      r.mutation.values ?? {},
      r.mutation,
      c,
    );
    await commit(env, [r.mutation], event ?? undefined);
  }
  return { ok: true, undo: r.undo };
}

export async function deleteExpense(env: CommandEnv, id: string): Promise<SpendingResult> {
  const entry = await repo.getEntry(env.db, env.userId, id);
  if (!entry) return { ok: false, error: 'not_found' };
  if (!canDelete(entry)) return { ok: false, error: 'not_deletable' };
  await commit(env, [buildDeleteExpense(id, ctx(env))]);
  return { ok: true };
}

/** Undo of a delete: a plain insert on the soft-deleted id (conventions §14), never ifAbsent. */
export async function restoreExpense(env: CommandEnv, id: string): Promise<SpendingResult> {
  await commit(env, [buildRestoreExpense(id, ctx(env))]);
  return { ok: true };
}

export async function bulkCategorize(
  env: CommandEnv,
  ids: string[],
  categoryId: string | null,
): Promise<SpendingResult> {
  const ms = buildBulkCategorize(ids, categoryId, ctx(env));
  if (ms.length > 0) await commit(env, ms);
  return { ok: true };
}

export async function addCategory(
  env: CommandEnv,
  name: string,
): Promise<SpendingResult<{ id: string }>> {
  const r = buildAddCategory(name, await repo.listCategories(env.db, env.userId), ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true, id: r.categoryId };
}

export async function renameCategory(
  env: CommandEnv,
  id: string,
  name: string,
): Promise<SpendingResult> {
  const r = buildRenameCategory(id, name, await repo.listCategories(env.db, env.userId), ctx(env));
  if (!r.ok) return r;
  await commit(env, [r.mutation]);
  return { ok: true };
}

export async function setCategoryHidden(
  env: CommandEnv,
  id: string,
  hidden: boolean,
): Promise<SpendingResult> {
  await commit(env, [buildSetCategoryHidden(id, hidden, ctx(env))]);
  return { ok: true };
}

/** Soft delete; entries keep their categoryId and read as uncategorized. Undo = restoreCategory. */
export async function deleteCategory(env: CommandEnv, id: string): Promise<SpendingResult> {
  await commit(env, [buildDeleteCategory(id, ctx(env))]);
  return { ok: true };
}

export async function restoreCategory(env: CommandEnv, id: string): Promise<SpendingResult> {
  await commit(env, [buildRestoreCategory(id, ctx(env))]);
  return { ok: true };
}

export async function reorderCategories(
  env: CommandEnv,
  orderedIds: string[],
): Promise<SpendingResult> {
  const ms = buildReorderCategories(
    orderedIds,
    await repo.listCategories(env.db, env.userId),
    ctx(env),
  );
  if (ms.length > 0) await commit(env, ms);
  return { ok: true };
}

/**
 * Runs whenever Spending becomes usable (Task 29 registers it with onModuleUsable).
 * Insert-if-absent with seeded ids, so it never overwrites a row, even one this device hasn't pulled yet.
 */
export async function seedSpendingDefaults(env: CommandEnv): Promise<void> {
  const { db, userId } = env;
  const settings = await repo.getUserSettings(db, userId);
  if (settings?.seeds[SPENDING_SEED]) return;
  const c = ctx(env);
  const keys = await repo.listCategoryKeys(db, userId);
  const tileTypes = new Set(await repo.listTileTypes(db, userId, SPENDING_DASHBOARD_ID));
  const seeds = { ...settings?.seeds, [SPENDING_SEED]: c.now };
  const settingsId = singletonId(userId, 'user_settings');
  const marker: Mutation = settings
    ? {
        id: newId(),
        table: 'user_settings',
        op: 'update',
        rowId: settingsId,
        values: { seeds },
        at: c.now,
        source: 'spending',
      }
    : {
        id: newId(),
        table: 'user_settings',
        op: 'insert',
        rowId: settingsId,
        values: { weekStart: 1, seeds },
        at: c.now,
        source: 'spending',
        ifAbsent: true,
      };
  await commit(env, [
    ...buildSeedCategories(userId, keys, c),
    ...buildSpendingDashboardTiles(userId, tileTypes, c),
    marker,
  ]);
}

/**
 * v0.1.1: soft-deletes the old Cigarettes and Loan payment defaults when no entry uses them and the
 * user didn't rename them, then records RETIRE_DEFAULTS_SEED so it never runs again. Call it only
 * after this session has pulled, so entries made on other devices are already here.
 */
export async function retireUnusedDefaults(env: CommandEnv): Promise<void> {
  const { db, userId } = env;
  const settings = await repo.getUserSettings(db, userId);
  if (!settings || settings.seeds[RETIRE_DEFAULTS_SEED]) return;
  const c = ctx(env);
  const deletes = buildRetireUnusedDefaults(
    userId,
    await repo.listAllCategories(db, userId),
    await repo.listUsedCategoryIds(db, userId),
    c,
    // v0.3: Quit Smoking owns Cigarettes while it is on, and once it has made (or restored) the
    // category: turning Quit off and back on doesn't restore it again, so the cleanup must not
    // delete it in between.
    // v0.4: Payments owns Loan payment the same way.
    new Set([
      ...(env.usable('quit_smoking') || settings.seeds[QUIT_CATEGORY_SEED] ? ['cigarettes'] : []),
      ...(env.usable('payments') || settings.seeds[PAYMENTS_CATEGORY_SEED] ? ['loan_payment'] : []),
    ]),
  );
  const marker: Mutation = {
    id: newId(),
    table: 'user_settings',
    op: 'update',
    rowId: singletonId(userId, 'user_settings'),
    values: { seeds: { ...settings.seeds, [RETIRE_DEFAULTS_SEED]: c.now } },
    at: c.now,
    source: 'spending',
  };
  await commit(env, [...deletes, marker]);
}

/** Waits for this session's first successful sync (push + pull), then runs retireUnusedDefaults. */
export function retireUnusedDefaultsAfterSync(env: CommandEnv): Promise<void> {
  const status = env.sync.status;
  if (!status) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const run = () => {
      unsubscribe?.();
      retireUnusedDefaults(env).then(resolve, (err: unknown) => {
        console.warn('Tahi: retiring unused default categories failed', err);
        resolve();
      });
    };
    let unsubscribe: (() => void) | undefined;
    if (status.get().lastSyncedAt !== null) return run();
    unsubscribe = status.subscribe(() => {
      if (status.get().lastSyncedAt !== null) run();
    });
  });
}

/** The Spending usable hook: seed defaults now; the v0.1.1 cleanup once this session has synced. */
export async function onSpendingUsable(env: CommandEnv): Promise<void> {
  await seedSpendingDefaults(env);
  void retireUnusedDefaultsAfterSync(env);
}
